import React, { useState } from 'react';
import { PostsAPI } from '@/services/postsAPI';
import { Post } from '@/types/posts';
import Modal from '@/components/Modal';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import Button from '@/components/Button';
import Alert from '@/components/Alert';

interface DeleteConfirmModalProps {
  post: Post;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  post,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      await PostsAPI.deletePost(post.id);
      onSuccess();
    } catch (err: any) {
      console.error('Error deleting post:', err);
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to delete post',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal size="sm" title="Delete Post" onRequestClose={onClose}>
      <Card.Section>
        <Stack gap={16} vertical>
          {error && <Alert description={error} status="danger" title="Error" />}

          <Body>Are you sure you want to delete this post?</Body>

          <div
            style={{
              padding: '12px',
              background: '#f5f5f5',
              borderRadius: '4px',
            }}
          >
            <Body weight="medium">{post.title_en}</Body>
            <Body>{post.excerpt_en.substring(0, 100)}...</Body>
          </div>

          <Alert
            description="This action cannot be undone."
            status="warning"
            title="Warning"
          />

          <Stack gap={12} justify="end">
            <Button a11yLabel="Cancel" label="Cancel" onAction={onClose} />
            <Button
              a11yLabel="Delete Post"
              isLoading={loading}
              label="Delete"
              onAction={handleDelete}
            />
          </Stack>
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default DeleteConfirmModal;
